import { useCallback, useSyncExternalStore } from "react";

/**
 * ef-062-6 - the last error a register row's check controls showed, kept OUTSIDE the component.
 *
 * Why: the register's `refresh()` flips the whole list to "loading", which unmounts every row and
 * mounts fresh ones, wiping component state. A refused Print (409: another window printed the check
 * first) must keep its plain-words alert on screen AFTER that re-read, until the farmer's next action
 * on the row - so the message lives here, keyed by the register line, and the remounted row reads it.
 * A row's message is removed by its own next action (`setNotice(null)`), so nothing accumulates.
 */
const notices = new Map<string, string>();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function write(key: string, message: string | null): void {
  if (message === null) {
    if (!notices.delete(key)) return;
  } else {
    notices.set(key, message);
  }
  listeners.forEach((l) => l());
}

export function useRegisterCheckNotice(entryLineId: string): [string | null, (message: string | null) => void] {
  const message = useSyncExternalStore(
    subscribe,
    () => notices.get(entryLineId) ?? null,
    () => null,
  );
  const setNotice = useCallback((next: string | null) => write(entryLineId, next), [entryLineId]);
  return [message, setNotice];
}
